import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";

import { RawTimelineEvent } from "@app/types/events/TimelineEvent";
import getCheckInEntityRef from "@app/util/timeline/getCheckInEntityRef";

import UserLocation from "../types/UserLocation";
import UserLocationCheckIn from "../types/UserLocationCheckIn";

import getEventDayKey from "./getEventDayKey";

/**
 * Adapts a Field View timeline event (any audit row that carries a `geoPoint`) into the Field View
 * location shape consumed by the User Locations map control. Caller MUST pre-filter to events whose
 * `geoPoint` is populated. Check-in events additionally carry a `checkIn` descriptor used by the
 * CheckInMarkers layer (the visited record's resolved map pin); this event's own `geoPoint` stays the
 * rep GPS, which still feeds the polyline. accountPins/contactPins resolve the check-in's entity.
 */
const convertTimelineEventToLocation = (
  event: RawTimelineEvent & { geoPoint: GeoPoint },
  orgTimeZone: string | undefined,
  accountPins: Record<Company["id"], Company & { shape?: MarkerShape }>,
  contactPins: Record<Person["id"], Person & { shape?: MarkerShape }>
): UserLocation => {
  // A check-in is a crm-activity timeline event whose checkInType points at the visited company/person.
  // Resolve that entity's map pin (from accountPins/contactPins) and carry it on the check-in so the
  // marker/tooltip/export all read one resolved object. The pin is fetched asynchronously, so a check-in
  // only materializes once its pin is present — before that the event is just a trail point.
  const checkInRef = getCheckInEntityRef(event);

  let checkIn: undefined | UserLocationCheckIn;
  if (checkInRef) {
    const entityPin =
      checkInRef.entityType === EntityType.COMPANY
        ? accountPins[checkInRef.entityId]
        : contactPins[checkInRef.entityId];
    if (entityPin) {
      const { activity } = checkInRef;
      checkIn = {
        activityId: activity.id,
        activityName: activity.name ?? activity.crmActivityType?.name ?? "",
        checkinAt: activity.checkinTime ?? null,
        checkoutAt: activity.checkoutTime ?? null,
        entityPin,
        reliability: activity.reliability === true,
        unreliableReasons: activity.unreliableReasons ?? [],
      };
    }
  }

  return {
    id: event.id,
    checkIn,
    createdAt: event.updatedAt,
    createdAtIsoDate: getEventDayKey(event.updatedAt, orgTimeZone),
    geoPoint: event.geoPoint,
    userId: event.updatedBy.id,
  };
};

export default convertTimelineEventToLocation;
